import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Link from "@mui/icons-material/Link";
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { dayjs, NhsNumberSchema } from "@thalamos/common";
import type {
  EnhancedPatientDemographicsAlert,
  EnhancedPatientSuggestedMerge,
} from "@thalamos/aspire-common";

import { ConfirmationModal } from "../ConfirmationModal.js";
import type { DefaultPageProps } from "../defaultProps.js";
import { api, apiHooks } from "~/api.js";
import {
  Button,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import type { MultiSelectFilterPickerOption } from "~/components/form/MultiSelectFilterPicker.js";
import { MultiSelectFilterPicker } from "~/components/form/MultiSelectFilterPicker.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { routeFns } from "~/routePaths.js";

export type DemographicsManagerHomePageProps = DefaultPageProps;

function SuggestedMergeRow(props: {
  merge: EnhancedPatientSuggestedMerge;
  reloadAlerts: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <TableRow>
      <TableCell>{dayjs(props.merge.created).format("DD/MM/YYYY")}</TableCell>
      <TableCell>{props.merge.type}</TableCell>
      <TableCell>{props.merge.sourcePatientTrust?.name ?? "—"}</TableCell>
      <TableCell>
        {NhsNumberSchema.catch(t("common.unknown")).parse(
          props.merge.sourcePatient.nhsNumber,
        )}
      </TableCell>
      <TableCell>
        {props.merge.sourcePatient.name.family.toUpperCase()},{" "}
        {props.merge.sourcePatient.name.given}
      </TableCell>
      <TableCell>
        {props.merge.sourcePatient.dateOfBirth
          ? dayjs(props.merge.sourcePatient.dateOfBirth).format("DD/MM/YYYY")
          : "Unknown"}
      </TableCell>
      <TableCell>
        {NhsNumberSchema.catch(t("common.unknown")).parse(
          props.merge.targetPatient.nhsNumber,
        )}
      </TableCell>
      <TableCell>
        {props.merge.targetPatient.name.family.toUpperCase()},{" "}
        {props.merge.targetPatient.name.given}
      </TableCell>
      <TableCell>
        {props.merge.targetPatient.dateOfBirth
          ? dayjs(props.merge.targetPatient.dateOfBirth).format("DD/MM/YYYY")
          : "Unknown"}
      </TableCell>
      <TableCell>
        <Button
          label="Go to Merge"
          variant="contained"
          color="primary"
          onClick={() => {
            navigate(
              routeFns.patientMergeConfirmation(
                props.merge.sourcePatientId,
                props.merge.targetPatientId,
              ),
            );
          }}
        />
      </TableCell>
    </TableRow>
  );
}

function SuggestedMergesTable(props: {
  merges: EnhancedPatientSuggestedMerge[];
  reloadAlerts: () => void;
}) {
  if (props.merges.length === 0) {
    return <Typography>No suggested merges</Typography>;
  }

  return (
    <Stack
      sx={{
        gap: "1rem",
      }}
    >
      <Typography sx={{ fontWeight: "bold" }}>
        Suggested Patient Merges
      </Typography>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "background.lightGray" }}>
            <TableCell>Created Date</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Source Patient Trust</TableCell>
            <TableCell>Source Patient NHS Number</TableCell>
            <TableCell>Source Patient Name</TableCell>
            <TableCell>Source Patient Date of Birth</TableCell>
            <TableCell>Target Patient NHS Number</TableCell>
            <TableCell>Target Patient Name</TableCell>
            <TableCell>Target Patient Date of Birth</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.merges
            .sort((a, b) => (a.created < b.created ? 1 : -1))
            .map((merge) => (
              <SuggestedMergeRow
                merge={merge}
                reloadAlerts={props.reloadAlerts}
                key={merge.id}
              />
            ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

function DemographicsAlertsRow(props: {
  alert: EnhancedPatientDemographicsAlert;
  reloadAlerts: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { alert } = props;

  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);

  const getAlertReason = (alert: EnhancedPatientDemographicsAlert): string => {
    switch (alert.type) {
      case "invalidated":
        return "Invalidated NHS number";
      case "deceased":
        return "Deceased patient";
      default:
        return "Unknown";
    }
  };

  const clearPatientAlerts = async () => {
    const res = await api.patientDemographics.clearAlertsForPatient(
      alert.patientId,
    );

    if (res.status !== 204) {
      renderErrorToast({ message: "Failed to clear alerts" });
    } else {
      renderSuccessToast({ message: "Alerts cleared" });
      props.reloadAlerts();
    }
  };

  return (
    <TableRow>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => {
            setConfirmFn(null);
          }}
        />
      )}
      <TableCell>
        <IconButton
          onClick={() => {
            navigate(routeFns.patientHome(alert.patientId));
          }}
        >
          <Link />
        </IconButton>
      </TableCell>
      <TableCell>{dayjs(alert.created).format("DD/MM/YYYY")}</TableCell>
      <TableCell>{getAlertReason(alert)}</TableCell>
      <TableCell>{alert.trust?.name ?? "—"}</TableCell>
      <TableCell>
        {NhsNumberSchema.catch(t("common.unknown")).parse(alert.nhsNumber)}
      </TableCell>
      <TableCell>
        {alert.patient.name.family.toUpperCase()}, {alert.patient.name.given}
      </TableCell>
      <TableCell>
        {alert.patient.dateOfBirth
          ? dayjs(alert.patient.dateOfBirth).format("DD/MM/YYYY")
          : "Unknown"}
      </TableCell>
      <TableCell>
        {alert.type !== "deceased" && (
          <Stack direction="row" spacing={1}>
            <Button
              color="inherit"
              variant="contained"
              label="Merge Patient"
              onClick={() => {
                navigate(routeFns.patientMergeSearch(alert.patientId));
              }}
            />
            <Button
              label="Clear Patient Alerts"
              variant="contained"
              color="primary"
              onClick={() => {
                setConfirmFn({
                  message:
                    "This action will remove the NHS Number from the patient’s record. To reassign an NHS Number, merge this record into another record that contains the correct one.",
                  confirmFn: () => void clearPatientAlerts(),
                });
              }}
            />
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}

function PatientDemographicsAlertsTable(props: {
  alerts: EnhancedPatientDemographicsAlert[];
  reloadAlerts: () => void;
}) {
  if (props.alerts.length === 0) {
    return <Typography>No demographics alerts</Typography>;
  }

  return (
    <Stack
      sx={{
        gap: "1rem",
      }}
    >
      <Typography sx={{ fontWeight: "bold" }}>
        Patient Demographics Alerts
      </Typography>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "background.lightGray" }}>
            <TableCell></TableCell>
            <TableCell>Created Date</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Trust</TableCell>
            <TableCell>Patient NHS Number</TableCell>
            <TableCell>Patient Name</TableCell>
            <TableCell>Patient Date of Birth</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.alerts
            .sort((a, b) => (a.created < b.created ? 1 : -1))
            .map((alert) => (
              <DemographicsAlertsRow
                alert={alert}
                reloadAlerts={props.reloadAlerts}
                key={alert.id}
              />
            ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

export function DemographicsManagerHomePage(
  _props: DemographicsManagerHomePageProps,
) {
  const loggedInUserContext = useContext(LoggedInUserContext);
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();

  const qs = useMemo(() => new URLSearchParams(search), [search]);
  const trustIdsFromUrl = qs.get("trustIds");
  const selectedTrustIds = useMemo(
    () => (trustIdsFromUrl ? trustIdsFromUrl.split(",") : undefined),
    [trustIdsFromUrl],
  );

  const [{ data: trustsData }] = apiHooks.patientDemographics.getTrusts();

  const availableTrusts: MultiSelectFilterPickerOption[] = useMemo(
    () =>
      (trustsData?.availableTrusts ?? []).map((t) => ({
        id: t.trustId,
        name: t.name,
      })),
    [trustsData],
  );

  // Auto-select current trust on first load only
  const hasInitialisedTrusts = useRef(!!trustIdsFromUrl);
  useEffect(() => {
    if (hasInitialisedTrusts.current) return;
    if (
      trustsData?.currentTrustId &&
      !trustIdsFromUrl &&
      availableTrusts.length > 0
    ) {
      const currentTrust = availableTrusts.find(
        (t) => t.id === trustsData.currentTrustId,
      );
      if (currentTrust) {
        hasInitialisedTrusts.current = true;
        const params = new URLSearchParams();
        params.set("trustIds", currentTrust.id);
        navigate(`${routeFns.demographicsManagement()}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [trustsData?.currentTrustId, trustIdsFromUrl, availableTrusts, navigate]);

  const handleTrustChange = useCallback(
    (selected: MultiSelectFilterPickerOption[]) => {
      const params = new URLSearchParams();
      if (selected.length > 0) {
        params.set("trustIds", selected.map((t) => t.id).join(","));
      }
      navigate(`${routeFns.demographicsManagement()}?${params.toString()}`, {
        replace: true,
      });
    },
    [navigate],
  );

  const [{ data: alerts, loading: alertsLoading }, reloadAlerts] =
    apiHooks.patientDemographics.getAlerts(selectedTrustIds);

  return alertsLoading || !alerts ? (
    <CircularProgress />
  ) : (
    <Stack>
      <Typography variant="h5">
        Demographics Management for{" "}
        {loggedInUserContext?.user.sessionContext?.dataControllerName}
      </Typography>
      {availableTrusts.length > 0 && (
        <Box
          sx={{
            mt: "1rem",
            maxWidth: 400,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <MultiSelectFilterPicker
              label="Filter by trust"
              options={availableTrusts}
              value={availableTrusts.filter((t) =>
                selectedTrustIds?.includes(t.id),
              )}
              onChange={handleTrustChange}
              testId="demographicsTrustFilterPicker"
            />
          </Box>
          <Tooltip title={t("pages.demographicsManager.trustFilterTooltip")}>
            <InfoOutlined
              data-testid="demographicsTrustFilterInfo"
              sx={{ fontSize: "1.25rem", color: "text.secondary" }}
            />
          </Tooltip>
        </Box>
      )}
      <Card sx={{ mt: "2rem", padding: "1rem" }}>
        <SuggestedMergesTable
          merges={alerts.suggestedMerges}
          reloadAlerts={() => void reloadAlerts()}
        />
      </Card>
      <Card sx={{ mt: "2rem", padding: "1rem" }}>
        <PatientDemographicsAlertsTable
          alerts={alerts.alerts}
          reloadAlerts={() => void reloadAlerts()}
        />
      </Card>
    </Stack>
  );
}
